import { FormEvent, useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { leadsApi } from '@/core/api/leadsApi';
import { getErrorMessage } from '@/core/utils/errors';
import { AppIcon } from '@/shared/components/AppIcon';

type QuickLeadModalProps = {
  open: boolean;
  onClose: () => void;
};

const initialForm = {
  mobile: '',
  name: '',
  companyName: '',
};

/**
 * The intentionally small lead form used by staff on the shop floor.
 * More complete lead fields stay available in the standard lead form.
 */
export function QuickLeadModal({ open, onClose }: QuickLeadModalProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const mobileInputRef = useRef<HTMLInputElement>(null);
  const [form, setForm] = useState(initialForm);
  const [error, setError] = useState<string | null>(null);
  const [createdLeadId, setCreatedLeadId] = useState<string | null>(null);

  const createLead = useMutation({
    mutationFn: () => {
      const nameParts = form.name.trim().split(/\s+/).filter(Boolean);

      return leadsApi.create({
        mobile: form.mobile.trim(),
        firstName: nameParts[0] || null,
        lastName: nameParts.slice(1).join(' ') || null,
        companyName: form.companyName.trim() || null,
        source: 'Mobile quick capture',
        priority: 'normal',
      });
    },
    onSuccess: async (result) => {
      setCreatedLeadId(result.lead.id);
      setError(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['leads'] }),
        queryClient.invalidateQueries({ queryKey: ['dashboard'] }),
        queryClient.invalidateQueries({ queryKey: ['reports'] }),
      ]);
    },
    onError: (requestError) => setError(getErrorMessage(requestError)),
  });

  useEffect(() => {
    if (!open) {
      return;
    }

    setError(null);
    setCreatedLeadId(null);
    const timer = window.setTimeout(() => mobileInputRef.current?.focus(), 120);
    return () => window.clearTimeout(timer);
  }, [open]);

  if (!open) {
    return null;
  }

  const close = () => {
    if (createLead.isPending) {
      return;
    }

    setForm(initialForm);
    setError(null);
    setCreatedLeadId(null);
    onClose();
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!form.mobile.trim()) {
      setError('شماره همراه را وارد کنید.');
      mobileInputRef.current?.focus();
      return;
    }

    setError(null);
    createLead.mutate();
  };

  const startAnother = () => {
    setForm(initialForm);
    setCreatedLeadId(null);
    setError(null);
    window.setTimeout(() => mobileInputRef.current?.focus(), 0);
  };

  const openFullForm = () => {
    close();
    navigate('/leads?create=1');
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center sm:items-center" role="dialog" aria-modal="true" aria-label="ثبت سریع سرنخ">
      <button
        type="button"
        aria-label="بستن"
        className="absolute inset-0 cursor-default bg-slate-950/45 backdrop-blur-[1px]"
        onClick={close}
      />
      <section className="relative w-full max-w-lg rounded-t-3xl bg-white p-5 shadow-2xl dark:bg-slate-900 sm:rounded-3xl sm:p-6" dir="rtl">
        <div className="mx-auto mb-4 h-1.5 w-12 rounded-full bg-slate-200 dark:bg-slate-700 sm:hidden" />
        <div className="mb-5 flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold text-indigo-600 dark:text-indigo-300">کمتر از ۳۰ ثانیه</p>
            <h2 className="mt-1 text-lg font-black text-slate-950 dark:text-white">ثبت سریع سرنخ</h2>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">فقط شماره همراه کافی است؛ بقیه اطلاعات را بعداً تکمیل کنید.</p>
          </div>
          <button type="button" onClick={close} className="rounded-xl p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900 dark:hover:bg-slate-800 dark:hover:text-white" aria-label="بستن فرم">
            <AppIcon name="close" size={20} />
          </button>
        </div>

        {createdLeadId ? (
          <div className="space-y-4">
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-emerald-900 dark:border-emerald-900/70 dark:bg-emerald-950/40 dark:text-emerald-200">
              <div className="flex items-center gap-2 font-bold"><AppIcon name="check" size={19} /> سرنخ ثبت شد.</div>
              <p className="mt-1 text-sm">می‌توانید سرنخ بعدی را ثبت کنید یا جزئیات این مورد را کامل کنید.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <button type="button" onClick={startAnother} className="rounded-xl border border-slate-200 px-3 py-3 text-sm font-bold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800">ثبت سرنخ بعدی</button>
              <button type="button" onClick={() => { close(); navigate(`/leads?lead=${createdLeadId}`); }} className="rounded-xl bg-indigo-600 px-3 py-3 text-sm font-bold text-white hover:bg-indigo-700">تکمیل اطلاعات</button>
            </div>
          </div>
        ) : (
          <form className="space-y-4" onSubmit={submit}>
            <label className="block">
              <span className="mb-1.5 block text-sm font-bold text-slate-800 dark:text-slate-100">شماره همراه <span className="text-rose-600">*</span></span>
              <input
                ref={mobileInputRef}
                value={form.mobile}
                onChange={(event) => setForm((current) => ({ ...current, mobile: event.target.value }))}
                className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-base text-slate-950 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-800 dark:text-white dark:focus:ring-indigo-950"
                inputMode="tel"
                autoComplete="tel"
                dir="ltr"
                placeholder="09xxxxxxxxx"
              />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="mb-1.5 block text-sm font-bold text-slate-800 dark:text-slate-100">نام مخاطب</span>
                <input value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} className="w-full rounded-xl border border-slate-300 bg-white px-3 py-3 text-sm text-slate-950 outline-none focus:border-indigo-500 dark:border-slate-700 dark:bg-slate-800 dark:text-white" placeholder="اختیاری" />
              </label>
              <label className="block">
                <span className="mb-1.5 block text-sm font-bold text-slate-800 dark:text-slate-100">نام شرکت</span>
                <input value={form.companyName} onChange={(event) => setForm((current) => ({ ...current, companyName: event.target.value }))} className="w-full rounded-xl border border-slate-300 bg-white px-3 py-3 text-sm text-slate-950 outline-none focus:border-indigo-500 dark:border-slate-700 dark:bg-slate-800 dark:text-white" placeholder="اختیاری" />
              </label>
            </div>
            {error ? <p className="rounded-xl bg-rose-50 px-3 py-2 text-sm font-medium text-rose-700 dark:bg-rose-950/40 dark:text-rose-200">{error}</p> : null}
            <button type="submit" disabled={createLead.isPending} className="flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-3.5 text-sm font-black text-white shadow-lg shadow-indigo-600/20 transition hover:bg-indigo-700 disabled:cursor-wait disabled:opacity-60">
              <AppIcon name="plus" size={18} />
              {createLead.isPending ? 'در حال ثبت…' : 'ثبت سرنخ'}
            </button>
            <button type="button" onClick={openFullForm} className="w-full py-1 text-sm font-semibold text-slate-500 hover:text-indigo-700 dark:text-slate-400 dark:hover:text-indigo-300">فرم کامل سرنخ را باز کن</button>
          </form>
        )}
      </section>
    </div>
  );
}
